import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';
import translations from './translations.js';

/**
 * The dot-dot-dot menu for notifications
 *
 * @prop resourceName - The title of menu
 * @prop resourceId - The ID of the notification
 * @prop markAsReadOn - Show 'Mark as read' action?
 * @prop markAsUnreadOn - Show 'Mark as unread' action?
 * @prop markAllAsReadOn - Show 'Mark all as read' action?
 * @prop settingsOn - Show "Notification settings"
 * @prop deleteOn - Show 'Delete' action?
 * @prop markAsReadUrl - Mark as read action URL
 * @prop markAsUnreadUrl - Mark as unread action URL
 * @prop markAllAsReadUrl - Mark all as read action URL
 * @prop settingsUrl - Leads to Notification settings page
 * @prop deleteUrl - Delete action URL
 *
 * @event notification-change - Notification has been read/unread (id of the notification is `target.resourceId`)
 * @event notification-delete - Notification has been deleted (id of the notification is `target.resourceId`)
 * @event notification-all-read - All notifications are marked as read
 */

export default class NotificationMenu extends BaseMenu {
  static properties = {
    resourceName: { type: String },
    resourceId: { type: Number },
    markAsReadOn: { type: Boolean },
    markAsUnreadOn: { type: Boolean },
    markAllAsReadOn: { type: Boolean },
    settingsOn: { type: Boolean },
    deleteOn: { type: Boolean },
    markAsReadUrl: { type: String },
    markAllAsReadUrl: { type: String },
    markAsUnreadUrl: { type: String },
    settingsUrl: { type: String },
    deleteUrl: { type: String },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get instanceId() {
    return this.resourceId;
  }

  /** @internal */
  get markAsReadItem() {
    return {
      title: this.t('actions.mark_as_read'),
      action: this.actionFor('actionWithEvent', [
        'patch',
        this.markAsReadUrl,
        'notification-change',
      ]),
      icon: 'envelope',
      partName: 'mark-as-read',
    };
  }

  /** @internal */
  get markAllAsReadItem() {
    return {
      title: this.t('actions.mark_all_as_read'),
      action: this.actionFor('actionWithEvent', [
        'post',
        this.markAllAsReadUrl,
        'notification-all-read',
      ]),
      icon: 'envelope',
      partName: 'mark-as-read',
    };
  }

  /** @internal */
  get markAsUnreadItem() {
    return {
      title: this.t('actions.mark_as_unread'),
      action: this.actionFor('actionWithEvent', [
        'patch',
        this.markAsUnreadUrl,
        'notification-change',
      ]),
      icon: 'envelope',
      partName: 'mark-as-unread',
    };
  }

  /** @internal */
  get settingsItem() {
    return {
      title: this.t('actions.settings'),
      action: this.actionFor('visit', [this.settingsUrl]),
      icon: 'cog',
      partName: 'notification-settings',
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('actions.delete'),
      action: this.actionFor('actionWithEvent', [
        'delete',
        this.deleteUrl,
        'notification-delete',
      ]),
      icon: 'delete',
      role: 'destructive',
      partName: 'delete destructive',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get dividerOn() {
    return (
      (this.markAsReadOn ||
        this.markAsUnreadOn ||
        this.markAllAsReadOn ||
        this.settingsOn) &&
      this.deleteOn
    );
  }

  /** @internal */
  get items() {
    return [
      this.markAsReadOn && this.markAsReadItem,
      this.markAllAsReadOn && this.markAllAsReadItem,
      this.markAsUnreadOn && this.markAsUnreadItem,
      this.settingsOn && this.settingsItem,
      this.dividerOn && this.divider,
      this.deleteOn && this.deleteItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.resourceName,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'bu-notification-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  async actionWithEvent(action, url, eventName) {
    await this.commonActions[action](url);
    /** @internal */
    this.dispatchEvent(new Event(eventName, { bubbles: true, composed: true }));
  }

  visit(url) {
    this.commonActions.visit(url);
  }

  render() {
    return html`
      <ef-dot-menu
        .config=${this.dotMenuConfig}
        buttonClass="bu-btn btn-naked-dark">
      </ef-dot-menu>
    `;
  }
}

NotificationMenu.registerTagName('bu-notification-menu');
NotificationMenu.registerCommonActions('bu-notification-menu');

customElements.get('bu-notification-menu') ||
  customElements.define('bu-notification-menu', NotificationMenu);
